import { PageHeader, useExport } from "@dl/web-core-js/shell";
import {
  Button,
  Dialog,
  Field,
  Input,
  MenuButton,
  Segmented,
  Select,
  toast,
  useConfirm,
} from "@dl/web-core-js/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Printer, RefreshCw, Truck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LABEL_SHEET_TYPES, useApi, type LabelSheetType } from "../../api";
import { AccessDenied, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";
import { LegacyProductionJobsGrid } from "./LegacyProductionJobsGrid";
import { ProductionJobTree, type TreeRow } from "./ProductionJobTree";

type Queue = "legacy" | "current";

/**
 * The whole production queue — both of them, behind one switch.
 *
 * The UFRA-era queue is still what the floor runs on day to day, so it opens
 * first; the current queue is a switch away rather than a second menu entry.
 * Each queue keeps its own permission: an account that may see only one gets
 * that one, with no switch to show.
 *
 * Printed / Shipped in the header take a job number typed by the operator —
 * that is how the floor reports a job that came back from a printer without
 * anyone having the grid open. Regenerate image resets the selected lines to
 * PENDING so the renderer picks them up again. Both act on the current queue,
 * so they show only there.
 */
export function ProductionJobsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { start: startExport } = useExport();
  const canCurrent = useAllowed(LogicView.PRODUCTION_JOB);
  const canLegacy = useAllowed(LogicView.PRODUCTION_LEGACY_JOB);
  const allowed = canCurrent || canLegacy;
  const title = t("navigation.item.production-jobs", "Production jobs");

  const [queue, setQueue] = useState<Queue>(canLegacy ? "legacy" : "current");
  const queues = [
    ...(canLegacy ? [{ value: "legacy" as const, label: t("production.queue.legacy", "Legacy") }] : []),
    ...(canCurrent ? [{ value: "current" as const, label: t("production.queue.current", "Current") }] : []),
  ];
  const queueSwitch = queues.length > 1 ? <Segmented value={queue} onChange={setQueue} options={queues} /> : undefined;

  const [selected, setSelected] = useState<TreeRow[]>([]);
  const [markMode, setMarkMode] = useState<"printed" | "shipped" | null>(null);
  const [jobNumber, setJobNumber] = useState("");
  const [cardJobId, setCardJobId] = useState<number | null>(null);
  const [labelSheet, setLabelSheet] = useState<LabelSheetType>("OL75WX");

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["production", "job"] });

  const markJob = async () => {
    const id = Number(jobNumber);
    if (!Number.isFinite(id) || id <= 0) return;
    try {
      const ok =
        markMode === "printed"
          ? await api.productionJobs.printed([id])
          : await api.productionJobs.shipped([id]);
      if (ok) {
        toast.success(
          markMode === "printed"
            ? t("notification.production.job.printed.success", "Production job marked as printed successfully")
            : t("notification.production.job.shipped.success", "Production job marked as shipped successfully"),
        );
        await refresh();
      } else {
        toast.error(
          markMode === "printed"
            ? t("notification.production.job.printed.error", "Failed to mark production job as printed")
            : t("notification.production.job.shipped.error", "Failed to mark production job as shipped"),
        );
      }
      setMarkMode(null);
      setJobNumber("");
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed"));
    }
  };

  const regenerateImage = async () => {
    const lines = selected.filter((row) => row.kind === "LINE");
    if (lines.length === 0) return;
    const ok = await confirm({
      title: t("dialog.confirm.regenerate-image.title", "Regenerate image"),
      message: t(
        "dialog.confirm.regenerate-image.text",
        "Are you sure you want to regenerate this production image?",
      ),
    });
    if (!ok) return;
    try {
      for (const row of lines) {
        if (row.kind !== "LINE") continue;
        await api.productionJobLines.update({ ...row.line, status: "PENDING" });
      }
      toast.success(
        t("notification.regenerate-image.started", "Production image regeneration started"),
      );
      await refresh();
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed"));
    }
  };

  if (!allowed) return <AccessDenied title={title} />;

  return (
    <>
      <PageHeader
        title={title}
        actions={
          queue === "current" && (
            <div className="flex items-center gap-2">
              <MenuButton
                label={t("menu.item.root.options", "Options")}
                items={[
                  {
                    label: t("menu.item.regenerate-image", "Regenerate image"),
                    icon: <RefreshCw className="size-3.5" />,
                    disabled: selected.filter((row) => row.kind === "LINE").length === 0,
                    onClick: () => void regenerateImage(),
                  },
                ]}
              />
              <Button
                size="sm"
                variant="primary"
                icon={<Printer className="size-3.5" />}
                onClick={() => setMarkMode("printed")}
              >
                {t("button.print", "Print")}
              </Button>
              <Button
                size="sm"
                variant="primary"
                icon={<Truck className="size-3.5" />}
                onClick={() => setMarkMode("shipped")}
              >
                {t("button.ship", "Ship")}
              </Button>
            </div>
          )
        }
      />

      {queue === "legacy" ? (
        <LegacyProductionJobsGrid toolbarLead={queueSwitch} />
      ) : (
        <ProductionJobTree
          selectable
          onSelectionChange={setSelected}
          onPrintJobCard={(jobId) => setCardJobId(jobId)}
          toolbarLead={queueSwitch}
        />
      )}

      <Dialog
        open={markMode !== null}
        onClose={() => setMarkMode(null)}
        title={
          markMode === "printed"
            ? t("dialog.confirm.printed.title", "Mark as printed")
            : t("dialog.confirm.shipped.title", "Mark as shipped")
        }
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setMarkMode(null)}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button variant="primary" disabled={!jobNumber} onClick={() => void markJob()}>
              {t("dialog.confirm", "Confirm")}
            </Button>
          </>
        }
      >
        <Field label={t("column.id", "id")} required>
          <Input
            type="number"
            autoFocus
            value={jobNumber}
            onChange={(event) => setJobNumber(event.target.value)}
          />
        </Field>
      </Dialog>

      <Dialog
        open={cardJobId !== null}
        onClose={() => setCardJobId(null)}
        title={t("dialog.print-job-card.title", "Print job card")}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setCardJobId(null)}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (cardJobId === null) return;
                void startExport(`job-card-${cardJobId}`, () =>
                  api.productionJobs.getJobCard(cardJobId, labelSheet),
                );
                setCardJobId(null);
              }}
            >
              {t("dialog.confirm.print", "Print")}
            </Button>
          </>
        }
      >
        <Field label={t("form.field.label-sheet-type", "Label sheet type")} required>
          <Select
            value={labelSheet}
            options={LABEL_SHEET_TYPES.map((entry) => ({ value: entry, label: entry }))}
            onChange={(event) => setLabelSheet(event.target.value as LabelSheetType)}
          />
        </Field>
      </Dialog>
    </>
  );
}
